import * as React from 'react';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { MenuItem } from '@mui/material';

/**
 * A single autocomplete option. Either a plain string, or an object whose
 * value/label are read via {@link MAutoCompleteProps.valueField} /
 * {@link MAutoCompleteProps.displayField} (falling back to `id`/`name`/`value`).
 */
type MAutoCompleteOption = string | Record<string, string | number | undefined>;

/**
 * Props for {@link MAutoComplete}.
 */
interface MAutoCompleteProps {
    /** Available options. Defaults to `[]`. */
    choices?: MAutoCompleteOption[];
    /** `TextField` variant used for the input. Defaults to `'standard'`. */
    variant?: 'standard' | 'outlined' | 'filled';
    /** Whether multiple tags can be selected. Defaults to `true`. */
    isMulti?: boolean;
    /** Whether the input is rendered in an error state. */
    error?: boolean;
    /** Helper text rendered below the input. */
    helperText?: React.ReactNode;
    /** Field label. */
    label?: React.ReactNode;
    /** Pre-selected option(s) applied when they are first available. */
    defaultValue?: MAutoCompleteOption[];
    /** Controlled selected option(s). */
    value?: MAutoCompleteOption[];
    /**
     * Called with the newly selected option(s).
     *
     * @param values - The full list of currently selected options.
     */
    onChange: (values: MAutoCompleteOption[]) => void;
    /**
     * Optional per-tag background color resolver.
     *
     * @param optionKey - The lower-cased string form of the tag's option.
     * @returns A CSS color value, or `undefined`/falsy for the default background.
     */
    getStyles?: (optionKey: string) => string | undefined;
    /** Ref forwarded to the rendered `TextField`'s input. */
    refInput?: React.Ref<HTMLInputElement>;
    /** Key read from an option object to obtain its underlying value. */
    valueField?: string;
    /** Key read from an option object to obtain its display label. */
    displayField?: string;
    /** Additional props forwarded to the underlying `TextField` and DOM elements. */
    id?: string;
    name?: string;
    className?: string;
    style?: React.CSSProperties;
    sx?: React.ComponentProps<typeof TextField>['sx'];
    disabled?: boolean;
}

/**
 * Reads an option's display or value field, falling back through the
 * conventional `name`/`id`/`value` keys used across this option shape.
 */
function readOptionField (option: MAutoCompleteOption, field: string | undefined, fallbackKeys: string[]): string {
    if (typeof option === 'string') return option;
    if (field && option[field] !== undefined) return String(option[field]);
    for (const key of fallbackKeys) {
        if (option[key] !== undefined) return String(option[key]);
    }
    return '';
}

/**
 * A MUI {@link Autocomplete} configured as a free-solo, multi-select tag input
 * with `Chip` rendering, custom per-tag coloring, and a `choices` / `value` /
 * `defaultValue` / `onChange` contract consistent with the other controls.
 */
const MAutoComplete: React.FC<MAutoCompleteProps> = ({
    choices = [],
    variant = 'standard',
    isMulti = true,
    error, helperText, label, defaultValue, value, onChange, getStyles, refInput,
    valueField, displayField,
    ...props
}) => {
    const [mvalue, setMValue] = React.useState<MAutoCompleteOption[]>(value || []);
    const [mDefaultValue, setMDefaultValue] = React.useState<MAutoCompleteOption[]>(defaultValue || []);

    const onTagsChange = (_event: React.SyntheticEvent, values: MAutoCompleteOption[]) => {
        setMValue(values);
        onChange(values);
    };

    React.useEffect(() => {
        if (defaultValue?.length) {
            setMDefaultValue(defaultValue);
            setMValue(defaultValue);
        }
    }, [defaultValue]);

    React.useEffect(() => {
        if (value?.length) setMValue(value);
    }, [value]);

    return (
        <Autocomplete
            {...props}
            style={{ width: '100%' }}
            freeSolo
            openOnFocus
            autoHighlight
            multiple={isMulti}
            disableCloseOnSelect={isMulti}
            options={choices}
            value={mvalue}
            defaultValue={mDefaultValue}
            onChange={onTagsChange as any}
            getOptionLabel={(option) => readOptionField(option as MAutoCompleteOption, displayField, ['name', 'value'])}
            renderTags={(tagsValue: MAutoCompleteOption[], getTagProps) =>
                tagsValue.map((option, index) => {
                    const key = readOptionField(option, valueField, ['id']) || readOptionField(option, undefined, []);
                    const label = readOptionField(option, displayField, ['name']);
                    const { key: _muiKey, ...tagProps } = getTagProps({ index });
                    return (
                        <Chip
                            style={getStyles ? { backgroundColor: getStyles(option.toString().toLowerCase()) } : {}}
                            key={key || label || String(option)}
                            label={label || String(option)}
                            size="small"
                            {...tagProps}
                        />
                    );
                })
            }
            isOptionEqualToValue={(option, value) => typeof option === 'string'
                ? option === value
                : readOptionField(option, displayField, ['name', 'value']) === readOptionField(value as MAutoCompleteOption, displayField, ['name', 'value'])}
            renderInput={(params) => (
                <TextField
                    {...params}
                    sx={{
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                            minWidth: 10
                        }
                    }}
                    ref={refInput}
                    variant={variant}
                    error={error}
                    label={label}
                    helperText={helperText}
                />
            )}
            renderOption={(optionProps, option, state) => (
                <MenuItem {...optionProps}
                    selected={state.inputValue === optionProps.key}
                    divider={true}
                    value={readOptionField(option as MAutoCompleteOption, valueField, ['id', 'value'])}
                >
                    {readOptionField(option as MAutoCompleteOption, displayField, ['name', 'label'])}
                </MenuItem>
            )}
        />
    );
};

export default React.memo(MAutoComplete);
